import createClient, { type Middleware } from 'openapi-fetch'
import type { paths } from '@/../types/api'
import { API_BASE_URL, apiUrl } from '@/lib/apiUrl'
import { API_UNAVAILABLE_MESSAGE, ApiError, isFetchFailure, type ProblemDetail } from '@/lib/errors'

/**
 * Token provider injected by App.tsx after `<ClerkProvider>` hydrates.
 * Default no-op so non-React consumers (tests, scripts) don't break — they
 * just send unauthenticated requests and get 401 from the backend.
 */
let getAuthToken: () => Promise<string | null> = () => Promise.resolve(null)

export function setAuthTokenProvider(fn: () => Promise<string | null>): void {
  getAuthToken = fn
}

/**
 * Attaches `Authorization: Bearer <token>` if a Clerk session is active.
 * Clerk's `getToken()` handles refresh internally — by the time it returns
 * the token is always usable (or null if signed out).
 */
const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = await getAuthToken()
    if (token) request.headers.set('Authorization', `Bearer ${token}`)
    return request
  },
}

/**
 * Authenticated `fetch` for endpoints that can't go through the typed
 * `api` client — typically `multipart/form-data` uploads (thumbnail,
 * image generation, mirage). Identical surface to the global `fetch`,
 * adds the Bearer header so requests aren't 401'd by the backend.
 *
 * Prefer `api.GET/POST(...)` whenever the OpenAPI schema covers the
 * endpoint; reach for this only when you need raw `Request`/`FormData`.
 */
export async function authFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const token = await getAuthToken()
  const headers = new Headers(init.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  // Relative-string inputs get prefixed with API_BASE_URL — same behavior as
  // the typed ``api`` client. Request/URL/absolute-string inputs go through
  // unchanged because the caller already chose the origin.
  const resolved = typeof input === 'string' ? apiUrl(input) : input
  try {
    return await fetch(resolved, { ...init, headers })
  } catch (err) {
    throw toApiNetworkError(err)
  }
}

/**
 * A rejected ``fetch`` (network down, tunnel down, or a 5xx gateway page
 * without CORS headers) surfaces as the browser's ``TypeError`` — "Failed
 * to fetch", "Load failed"… Turn it into an ``ApiError`` with status 0 so
 * the UI classifies it as "server unreachable" (``errorKind`` → network).
 * Only the network ``TypeError``s (``isFetchFailure``): one for an invalid
 * URL or header is a bug and passes through, like ``AbortError``.
 */
export function toApiNetworkError(err: unknown): unknown {
  if (isFetchFailure(err)) return new ApiError(API_UNAVAILABLE_MESSAGE, 0, null, null)
  return err
}

/** Bytes-on-the-wire snapshot of an upload still in flight. */
export interface UploadProgress {
  loaded: number
  /** Total bytes to send; ``0`` when the browser can't compute it. */
  total: number
  /** 0–100, or ``null`` while ``total`` is unknown. */
  percent: number | null
}

export interface AuthUploadOptions {
  /** Fires as bytes leave the browser — roughly once per network chunk. */
  onProgress?: (progress: UploadProgress) => void
  /**
   * Fires when the last byte is out but the response hasn't arrived yet.
   * Upload endpoints keep working after that (probing, remuxing), so this
   * is the cue to stop showing a percentage and say the server is busy.
   */
  onUploaded?: () => void
  signal?: AbortSignal
}

/**
 * ``authFetch`` for multipart uploads that need a progress bar.
 *
 * ``fetch`` has no upload-progress event — the streaming request bodies
 * that would give one are Chromium-only and require HTTP/2 — so this runs
 * on ``XMLHttpRequest`` and rebuilds a ``Response`` from the result.
 * Callers read ``res.ok`` / ``res.json()`` exactly as with ``authFetch``.
 */
export async function authUpload(
  path: string,
  body: FormData,
  { onProgress, onUploaded, signal }: AuthUploadOptions = {},
): Promise<Response> {
  const token = await getAuthToken()

  return new Promise<Response>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Upload cancelado', 'AbortError'))
      return
    }

    const xhr = new XMLHttpRequest()
    xhr.open('POST', apiUrl(path))
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    // Content-Type is deliberately left alone: the browser has to set it
    // together with the multipart boundary.

    xhr.upload.addEventListener('progress', (event) => {
      const total = event.lengthComputable ? event.total : 0
      onProgress?.({
        loaded: event.loaded,
        total,
        percent: total > 0 ? Math.min(100, Math.round((event.loaded / total) * 100)) : null,
      })
    })
    xhr.upload.addEventListener('load', () => {
      onUploaded?.()
    })

    xhr.addEventListener('load', () => {
      // A fired ``load`` with status 0 means the response was blocked
      // (CORS) — ``new Response`` rejects that status, so translate here.
      if (xhr.status === 0) {
        reject(new ApiError('Sem resposta do servidor', 0, null, null))
        return
      }
      resolve(responseFromXhr(xhr))
    })
    xhr.addEventListener('error', () => {
      reject(new ApiError('Falha de rede durante o envio', 0, null, null))
    })
    xhr.addEventListener('timeout', () => {
      reject(new ApiError('Tempo esgotado durante o envio', 0, null, null))
    })
    xhr.addEventListener('abort', () => {
      reject(new DOMException('Upload cancelado', 'AbortError'))
    })

    signal?.addEventListener(
      'abort',
      () => {
        xhr.abort()
      },
      { once: true },
    )

    xhr.send(body)
  })
}

/** Repackage a finished XHR as a ``Response`` so upload callers can share
 * the same error/JSON handling as every ``fetch``-based endpoint. */
function responseFromXhr(xhr: XMLHttpRequest): Response {
  const headers = new Headers()
  for (const line of xhr.getAllResponseHeaders().trim().split(/[\r\n]+/)) {
    const sep = line.indexOf(':')
    if (sep > 0) headers.append(line.slice(0, sep), line.slice(sep + 1).trim())
  }
  // The Response constructor throws if a null-body status carries one.
  const bodyless = xhr.status === 204 || xhr.status === 205 || xhr.status === 304
  return new Response(bodyless ? null : xhr.responseText, {
    status: xhr.status,
    statusText: xhr.statusText,
    headers,
  })
}

/**
 * Unwraps Problem Details from non-2xx responses and raises ApiError so
 * consumers can narrow on status / problem fields. Runs on every request.
 */
const errorMiddleware: Middleware = {
  async onResponse({ response }) {
    if (response.ok) return response
    throw await apiErrorFromResponse(response, '')
  },
  // Runs when ``fetch`` itself rejects — there is no response to unwrap.
  onError({ error }) {
    const converted = toApiNetworkError(error)
    return converted instanceof Error ? converted : undefined
  },
}

/**
 * Builds the ``ApiError`` for a non-2xx ``Response``: reads the Problem
 * Detail when the body is JSON, and never puts a non-JSON body (the HTML
 * of a Cloudflare 502) into the message — ``fallback`` goes there instead.
 * Endpoints that call ``authFetch`` directly throw this on ``!res.ok``.
 */
export async function apiErrorFromResponse(response: Response, fallback: string): Promise<ApiError> {
  let body: unknown = null
  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json') || contentType.includes('application/problem+json')) {
    try {
      body = await response.clone().json()
    } catch {
      /* non-JSON error body — leave as null */
    }
  }

  const problem = isProblemDetail(body) ? body : null
  const message = problem?.detail ?? problem?.title ?? fallback
  return new ApiError(message !== '' ? message : fallback, response.status, problem, body)
}

function isProblemDetail(body: unknown): body is ProblemDetail {
  if (!body || typeof body !== 'object') return false
  const obj = body as Record<string, unknown>
  return typeof obj['title'] === 'string' && typeof obj['status'] === 'number'
}

/**
 * Typed fetch client. Every path/operation is inferred from `paths` in
 * `types/api.ts`, which is regenerated by `scripts/gen_ts_client.sh`.
 *
 * ``baseUrl`` is read from ``VITE_API_BASE_URL`` (see ``lib/apiUrl.ts``).
 * Empty in dev so the Vite proxy can forward ``/api/v1`` and ``/auth``
 * to the local backend; set to the public API origin in the Cloudflare
 * Pages build so the deployed SPA reaches the backend through the
 * Cloudflare Tunnel.
 */
export const api = createClient<paths>({
  baseUrl: API_BASE_URL,
  credentials: 'same-origin',
})

// Order matters: auth runs first so the request leaves with a Bearer token,
// then errorMiddleware translates non-2xx into ApiError on the way back.
api.use(authMiddleware)
api.use(errorMiddleware)
